import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit
} from "@angular/core";
import { BreakpointObserver } from "@angular/cdk/layout";
import { combineLatest, Subscription } from "rxjs";
import { filter, map } from "rxjs/operators";
import { RecordingService } from "@onsip/common/services/api/resources/recording/recording.service";
import { AdvAnnouncementService } from "@onsip/common/services/api/resources/announcement/adv-announcement.service";
import { AdvAttendantMenuService } from "@onsip/common/services/api/resources/attendantMenu/attendant-menu.service";
import { MatDialog } from "@angular/material/dialog";
import { AdminTableAbstractComponent } from "../../abstractClass/admin-table-abstract.component";
import { TranslateService } from "@ngx-translate/core";
import { SnackbarService } from "../../shared/components/snackbar/snackbar.service";

const debug = false;

type RecordingAppType = string | "unassigned";

export interface RecordingTableData {
  recordingId: string;
  name: string;
  number: string;
  location: Array<RecordingAppType>;
  duration: string;
  recordingUrl: string;
  filePath: string;
  createdAt: string;
}

@Component({
  selector: "onsip-admin-recording",
  templateUrl: "./recording-library.component.html",
  styleUrls: ["./recording-library.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AdminRecordingLibraryComponent
  extends AdminTableAbstractComponent<RecordingTableData>
  implements OnInit, OnDestroy
{
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;

  recordingSet: Record<string, Set<RecordingAppType>> = {};
  defaultSortingColumn = "created";

  protected displayedColumnsRecord = {
    XSmall: ["name", "actions"],
    Small: ["name", "location", "duration", "actions"],
    Medium: ["name", "location", "duration", "actions"],
    Large: ["name", "location", "recordingUrl", "actions"],
    XLarge: ["name", "location", "recordingUrl", "actions"]
  };
  protected unsubscriber = new Subscription();

  constructor(
    private recordingService: RecordingService,
    private announcementService: AdvAnnouncementService,
    private attendantMenuService: AdvAttendantMenuService,
    private snackbar: SnackbarService,
    protected dialog: MatDialog,
    protected breakpointObserver: BreakpointObserver,
    protected cdr: ChangeDetectorRef,
    protected translate: TranslateService
  ) {
    super(breakpointObserver, cdr, dialog, translate);
  }

  ngOnInit() {
    super.ngOnInit();
    this.setDataFilter();
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    this.unsubscriber.unsubscribe();
  }

  trackByRecordingId(index: number, item: RecordingTableData): string {
    return item.recordingId;
  }

  protected getItemValueByColumn(item: RecordingTableData, columnName: string): string | number {
    switch (columnName) {
      case "name":
        return item.name.trim().toLowerCase();
      case "location":
        return item.location.join("").trim().toLowerCase();
      default:
        return new Date(item.createdAt).getTime();
    }
  }

  protected getItemName(): string {
    return "this recording";
  }

  protected performDeleteRequest({ recordingId }: RecordingTableData): void {
    this.recordingService.recordingDelete(recordingId).then(result => {
      if (result.status === "success") {
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_DELETED", {
            value: "Recording"
          }),
          "success"
        );
      } else {
        this.snackbar.openSnackBar(result.data.message, "error");
        this.recordingService.clearErrors();
      }
    });
  }

  protected initDataSource(): void {
    this.recordingService.recordingBrowse();
    this.announcementService.advAnnouncementBrowse();
    this.attendantMenuService.advAttendantMenuBrowse();

    this.unsubscriber.add(
      combineLatest({
        recordings: this.recordingService.orgState,
        announcements: this.announcementService.orgState,
        attendantMenus: this.attendantMenuService.state.pipe(
          filter(({ loading }) => !loading),
          map(state => Object.values(state.state))
        )
      }).subscribe(({ recordings, announcements, attendantMenus }) => {
        announcements.forEach(announcement => {
          const number = announcement.recordingNumber;
          const name = announcement.name;
          if (!this.recordingSet[number]) this.recordingSet[number] = new Set();
          this.recordingSet[number].add(`Announcement - ${name}`);
        });
        attendantMenus.forEach(attendantMenu => {
          const number = attendantMenu.recordingNumber;
          const name = attendantMenu.name;
          if (!this.recordingSet[number]) this.recordingSet[number] = new Set();
          this.recordingSet[number].add(`Attendant menu - ${name}`);
        });
        const dataSource = recordings.map(recording => {
          const location = this.recordingSet[recording.number];
          const fetchedUrl = this.recordingService.getRecordingUrl(recording.recordingId);
          debug && console.warn(fetchedUrl);
          const recordingUrl = fetchedUrl;
          return {
            name: recording.name,
            number: recording.number,
            location: location ? [...location] : ["unassigned"],
            duration: this.setDurationInFormat(recording.metadata.duration),
            recordingUrl,
            recordingId: recording.recordingId,
            filePath: recording.metadata.path,
            createdAt: recording.metadata.createdAt
          } as RecordingTableData;
        });
        this.dataSource.data = dataSource;
        this.isDataObtained$.next(true);
      })
    );
  }

  protected setDataFilter(): void {
    this.dataSource.filterPredicate = (data, filter) => {
      const filterValue = filter.trim().toLowerCase();
      const name = data.name.toLowerCase();
      const location = data.location.join("").toLowerCase();
      return name.includes(filterValue) || location.includes(filterValue);
    };
  }

  private setDurationInFormat(d: string): string {
    if (!d) return "00:00";
    const duration = Math.floor(Number(d));
    const minutes: number = Math.floor(duration / 60);
    return (
      minutes.toString().padStart(2, "0") +
      ":" +
      (duration - minutes * 60).toString().padStart(2, "0")
    );
  }
}
